import React, { FC, useState } from 'react';
import { Order } from 'src/interfaces/Table';
import interventionOrdersState from 'src/recoil/atoms/interventionOrders';
import InterventionOrder from 'src/interfaces/api/InterventionOrder';
import EntityTable from 'src/components/Table/EntityTable.container';
import { TableColumn } from 'src/components/Table/EntityTable';
import { CollectionResponse } from 'src/interfaces/api/api';
import Badge from 'src/components/Badge';
import { MdDone as BilledIcon } from '@react-icons/all-files/md/MdDone';
import useScreenBreakpoint from 'src/hooks/useMediaQuery';
import { IconButton } from '@material-ui/core';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

interface Props {
  onInvoiceInterventionOrder: (interventionOrderId: InterventionOrder['id']) => Promise<InterventionOrder>;
  onGetInterventionOrders: (
    page: number,
    limit: number,
    orderBy: keyof InterventionOrder,
    order: Order
  ) => Promise<CollectionResponse<InterventionOrder>>;
}

const columns: TableColumn<InterventionOrder>[] = [
  {
    id: 'socialReason',
    type: 'text',
    label: 'Comptes',
    link: (interventionOrder: InterventionOrder) => `/dashboard/intervention-orders/${interventionOrder.id}`,
  },
  {
    id: 'object',
    type: 'text',
    label: 'Objet',
    link: (interventionOrder: InterventionOrder) => `/dashboard/intervention-orders/${interventionOrder.id}`,
  },
  {
    id: 'statut',
    type: 'text',
    label: 'Statut',
    renderValue: (interventionOrder: InterventionOrder) => (
      <div>
        {interventionOrder.toBilled && (
          <Badge
            style={{ marginRight: 5, marginBottom: 5 }}
            color='warning'
            title={`${interventionOrder.price}€ H.T.`}
            text='A facturer'
          />
        )}
        {interventionOrder.hasContract && (
          <Badge style={{ marginRight: 5, marginBottom: 5 }} color='info' text='Contrat' />
        )}
      </div>
    ),
  },
  {
    id: 'createdAt',
    type: 'datetime',
    label: 'Crée le',
    sortable: true,
    link: (interventionOrder: InterventionOrder) => `/dashboard/intervention-orders/${interventionOrder.id}`,
  },
];

const InterventionOrderTable: FC<Props> = ({ onGetInterventionOrders, onInvoiceInterventionOrder }) => {
  const screenBreakPoint = useScreenBreakpoint();
  const [interventionToBilled, setInterventionToBilled] = useState<string>();

  const handleInterventionBilled = async () => {
    if (!interventionToBilled) throw new Error();

    await onInvoiceInterventionOrder(interventionToBilled);
    setInterventionToBilled(undefined);
  };

  return (
    <>
      <EntityTable
        columns={columns}
        defaultOrderBy='createdAt'
        atomState={interventionOrdersState}
        actions={(interventionOrder: InterventionOrder) => (
          <>
            {interventionOrder.toBilled && (
              <IconButton
                size='small'
                onClick={() => setInterventionToBilled(interventionOrder.id)}
                title='Passer en facturé'
              >
                <BilledIcon />
              </IconButton>
            )}
          </>
        )}
        fetchEntitiesCallback={onGetInterventionOrders}
        labelRowsPerPage={['xs', 'md'].includes(screenBreakPoint) ? ' ' : 'Element par page: '}
        labelDisplayedRows={
          ['xs', 'md'].includes(screenBreakPoint) ? ({ from, to, count }) => `${from}-${to}` : undefined
        }
      />

      <ConfirmationDialog
        title='Attention'
        confirmationText='Confirmer'
        description="Vous allez changer l'état de ce bon d'intervention en 'Facturé'. Êtes-vous sûr ?"
        open={!!interventionToBilled}
        onClose={() => setInterventionToBilled(undefined)}
        onValidate={handleInterventionBilled}
      />
    </>
  );
};

export default React.memo(InterventionOrderTable);
